import { useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthTextField, LockIcon } from './auth-text-field';

/**
 * Campo de palavra-passe com revelar/ocultar.
 *
 * O alternador é um `button` com `aria-pressed`, não um ícone clicável: o estado "revelada" é
 * informação, e um leitor de tela precisa de a ouvir. O foco volta ao campo depois de alternar —
 * sem isso, quem navega por teclado perde a posição do cursor a meio da escrita, que é
 * precisamente quando decide espreitar o que digitou.
 *
 * O alvo tem 44×44 px dentro da cápsula de 50 px (canvas §9 · `RNF-ACC-05`).
 */
export interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  error?: string | null;
  hint?: string;
  belowField?: ReactNode;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
}

export function PasswordField({ label, value, onChange, error, hint, belowField, id, ...rest }: PasswordFieldProps) {
  const { t } = useTranslation();
  const generatedId = useId();
  const fieldId = id ?? `${generatedId}-password`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [revealed, setRevealed] = useState(false);

  const toggle = () => {
    setRevealed((current) => !current);
    inputRef.current?.focus();
  };

  return (
    <AuthTextField
      ref={inputRef}
      id={fieldId}
      label={label}
      type={revealed ? 'text' : 'password'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      icon={<LockIcon />}
      error={error}
      hint={hint}
      belowField={belowField}
      action={
        <button
          type="button"
          onClick={toggle}
          aria-pressed={revealed}
          aria-controls={fieldId}
          aria-label={revealed ? t('auth.password.hide') : t('auth.password.show')}
          className="-mr-3 flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full text-ink-3 transition-colors duration-1 ease-soberano hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold focus-visible:ring-2"
        >
          {revealed ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 4l16 16" />
              <path d="M9.6 6.3A9.6 9.6 0 0112 6c6 0 9.5 6 9.5 6a16 16 0 01-3.3 4" />
              <path d="M6.2 8.3A16 16 0 002.5 12s3.5 6 9.5 6a9.5 9.5 0 003.6-.7" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      }
      {...rest}
    />
  );
}
